import { useState } from "react";
import { Alert } from "@/ui/alert";
import { useAcoes, useEstado, useOcupado } from "../estado.jsx";
import { hora } from "../visual.jsx";
import { Botao, Cortina, SeloIA } from "./comuns.jsx";

/**
 * A leitura do caso pela IA — um cartão claro com o verde da IA.
 *
 * O que saiu daqui: "RISCOS DETECTADOS" (era o MESMO prazo e o MESMO documento
 * que PENDÊNCIAS já mostra, recalculados e sem ordem de urgência),
 * "CONHECIMENTO DO CLIENTE" (as pastilhas eram os MESMOS campos da FICHA, dois
 * blocos abaixo — empresa, função, salário, admissão, escritos duas vezes na
 * mesma coluna) e as ações sugeridas, que foram para AGORA, embaixo do que o
 * servidor manda fazer: duas listas de "próximo passo" com o mesmo desenho
 * deixavam a pessoa sem saber qual clicar.
 *
 * O que ficou é o que só a IA sabe dizer: uma frase sobre o caso, o resumo, e o
 * placar dos fatos que vão para a peça. O rodapé diz QUANDO a leitura foi feita
 * — é o que separa "acabou de ler" de uma frase de meia hora atrás com cara de
 * texto fixo da tela.
 */
function Leitura({ ia, aoReler, ocupado }) {
  /*
   * WIDGET escuro que abre FOLHA — não colapsável.
   *
   * A regra da casa é "widget que aparece quando precisa, nunca colapsável"
   * (27/08), e aqui ela tem um motivo a mais: o que a IA entendeu do caso é
   * LEITURA, e leitura em coluna de 300px é três linhas cortadas com um "ler
   * tudo" embaixo. Na folha ela tem a largura de um parágrafo.
   *
   * Fechado o widget diz as três coisas que valem de relance — que a IA leu, o
   * que ela concluiu (uma linha) e o placar dos fatos. Toque abre o resto.
   */
  const [aberto, setAberto] = useState(false);
  /* Fato nenhum marcado não vira "0/0": zero sobre zero é o placar de um jogo
     que não começou, e no cartão parecia contagem quebrada. */
  const numeros = [
    ["Êxito", ia.exito],
    ["Fatos na peça", ia.fatos?.startsWith("0/0") ? null : ia.fatos],
  ].filter(([, valor]) => valor);
  /* `recado` é a exceção CRUA do motor ("Nenhum agente local conectado. Abra
     Ajustes → …"). Ela não é leitura do caso: é a IA dizendo que não leu. */
  const falhou = Boolean(ia.recado);

  return (
    <>
      {/* Cartão CLARO com um toque de verde (29/10): o bloco preto gritava mais
          alto que tudo na coluna, inclusive que o que precisa ser feito. O verde
          é o da IA em toda a mesa; o âmbar diz que ela não leu. */}
      <button
        type="button"
        data-secao="copiloto"
        onClick={() => setAberto(true)}
        data-dica="A leitura deste caso pela IA — toque para ler inteira e ler de novo"
        className={`flex w-full cursor-pointer flex-col gap-2 rounded-[16px] border p-[13px_14px] text-left text-foreground transition-[border-color,scale] duration-[var(--t-toque)] ease-entra active:scale-[.985] ${
          falhou
            ? "border-ambar/30 bg-ambar/8 hover:border-ambar/50"
            : "border-verde/22 bg-[linear-gradient(160deg,color-mix(in_oklab,var(--color-verde)_9%,var(--color-card)),var(--color-card)_72%)] hover:border-verde/40"
        }`}
      >
        <span className="flex w-full items-start gap-2.5">
          <SeloIA
            fundo={falhou ? "var(--color-ambar)" : "var(--color-verde-vivo)"}
            letra="var(--color-creme)"
            tamanho={22}
            fonte={9.5}
          />
          <span className="min-w-0 flex-1 text-corpo leading-[1.3] font-semibold text-balance">
            {falhou ? "A IA não leu este caso" : ia.titulo}
          </span>
          <span className="flex-none pt-px text-apoio text-tinta-fraca tabular-nums">
            {falhou ? "" : ia.deMemoria && ia.pensadoEm ? hora(ia.pensadoEm) : "agora"}
          </span>
        </span>

        {/* O RESUMO inteiro, na cara: é a leitura do caso, e cortá-lo devolvia
            a pergunta ("o que mais ela viu?") ao clique. */}
        {!falhou && ia.resumo ? <span className="text-corpo leading-[1.55] text-tinta-media">{ia.resumo}</span> : null}
        {falhou ? (
          <span className="text-apoio leading-[1.45] text-tinta-suave">
            O resumo é do banco, não do modelo — toque para ver o motivo.
          </span>
        ) : null}

        {numeros.length || ia.atrasada ? (
          <span className="flex flex-wrap gap-1.5">
            {numeros.map(([rotulo, valor]) => (
              <span key={rotulo} className="rounded-full bg-verde/10 px-2.5 py-0.5 text-apoio font-semibold text-verde">
                {rotulo} {valor}
              </span>
            ))}
            {ia.atrasada ? (
              <span className="rounded-full bg-foreground/6 px-2.5 py-0.5 text-apoio font-semibold text-tinta-suave">
                antes da última mudança
              </span>
            ) : null}
          </span>
        ) : null}
      </button>

      {aberto ? (
        <Cortina
          aoFechar={() => setAberto(false)}
          largura="520px"
          z={44}
          titulo={falhou ? "A IA não leu este caso" : "O que a IA entendeu"}
          antesDoTitulo={<SeloIA fundo="var(--color-verde)" letra="var(--color-creme)" tamanho={22} fonte={10} />}
          acima={
            !ia.deMemoria || !ia.pensadoEm
              ? "Lido agora."
              : ia.atrasada
                ? `Lido às ${hora(ia.pensadoEm)}. O caso mudou depois: a IA relê sozinha em alguns minutos, ou leia de novo agora.`
                : `Lido às ${hora(ia.pensadoEm)} — nada mudou no caso desde então.`
          }
          rodape={
            <Botao
              rotulo={ocupado ? "Lendo…" : "Ler de novo"}
              aoTocar={aoReler}
              desabilitado={ocupado}
              titulo="Ler o caso de novo, ignorando a leitura guardada"
            />
          }
        >
          {numeros.length ? (
            <div className="mb-3 flex items-baseline gap-4">
              {numeros.map(([rotulo, valor]) => (
                <span key={rotulo} className="flex items-baseline gap-1.5">
                  <span className="text-selo tracking-[.05em] text-muted-foreground">{rotulo}</span>
                  <span className="font-mono text-forte font-bold text-verde-fundo">{valor}</span>
                </span>
              ))}
            </div>
          ) : null}

          <div className="font-serif text-[20px] leading-[1.25] text-balance">{ia.titulo}</div>
          <p className="mt-2 text-corpo leading-[1.6] text-tinta-media">{ia.resumo}</p>

          {/* O texto técnico da falha: ele fala de terminal e de arquivo de
              configuração, e quem atende não resolve isso — quem cuida do
              servidor resolve, e é essa pessoa que abre esta folha. */}
          {falhou ? (
            <Alert
              role="status"
              className="mt-3.5 rounded-xl border-ambar/35 bg-ambar/11 p-3 text-apoio leading-[1.5] text-ambar-escuro"
            >
              {ia.recado}
            </Alert>
          ) : null}
        </Cortina>
      ) : null}
    </>
  );
}

export default function Copiloto() {
  const { ia, motor } = useEstado();
  const ocupado = useOcupado();
  const { relerCopiloto } = useAcoes();

  /* O resumo aparece em QUALQUER modo (Felipe, 08/10): ele saiu do Sem IA em
     27/09 e era o que a equipe lia embaixo das etiquetas. Resumir não fala com
     o cliente nem decide nada — o modo manda no que a IA FAZ, não no que ela
     lê. `semIA` só vem se alguém tirar a leitura da matriz de novo. */
  if (ia?.semIA) return null;

  if (!ia)
    return (
      /* Esqueleto, e não a frase "consultando…": o cartão escuro ocupa o mesmo
         espaço que vai ocupar, e a troca é o bloco ganhando conteúdo em vez de a
         coluna inteira se remontando quando a leitura chega. */
      <div
        data-secao="copiloto"
        role="status"
        aria-label={`Consultando o copiloto${motor?.nome ? ` (${motor.nome})` : ""}…`}
        className="animate-[vPulse_1.6s_ease-in-out_infinite] rounded-[16px] border border-verde/18 bg-card p-[14px]"
      >
        <div className="h-3.5 w-3/5 rounded-full bg-foreground/10" />
        <div className="mt-3 h-2.5 w-full rounded-full bg-foreground/7" />
        <div className="mt-1.5 h-2.5 w-4/5 rounded-full bg-foreground/7" />
      </div>
    );

  if (ia.erro)
    return (
      <div data-secao="copiloto">
        {/* `role="status"` e não o `alert` que o componente traz por padrão: o
            copiloto falhando não é resposta ao que a pessoa acabou de fazer — ela
            pode estar digitando para o cliente, e interromper a leitura com um
            aviso do assistente é tirar a atenção do que importa. */}
        <Alert role="status" className="rounded-xl border-ambar/30 bg-ambar/11 p-3 text-corpo leading-[1.5] text-ambar-escuro">
          {ia.erro}
        </Alert>
      </div>
    );

  return <Leitura ia={ia} aoReler={relerCopiloto} ocupado={ocupado === "copiloto"} />;
}
